import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import ClientSectionTabs from './ClientSectionTabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from './ui/select';
import { Textarea } from './ui/textarea';
import { stageClass, errMsg } from '../lib/api';
import { toast } from 'sonner';
import api from '../lib/api';
import { fmtDate } from '../lib/date';
import { PhoneCall, History, Languages, Trash2, MessageSquare, Phone, Copy, Check, RotateCcw } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

const YESNO = ['Yes', 'No'];
// Roles allowed to see + click the "Delete Client" button. Others don't even
// see it — the modal footer just shows Cancel + Save for them.
const DELETE_ROLES = new Set(['super_user', 'admin', 'hr_payroll']);
// Terminal outcomes that block the auto-transition to "Therapy Started".
const INACTIVE_OUTCOMES = new Set(['Withdrew', 'Lost', 'Discharged', 'Not Eligible', 'Financial/Ins Issue']);

// ── Phone → sms: / tel: URI helpers (iter 94 — "Text this family") ───────
// Strip everything except digits and a leading "+". Front-desk admins
// enter numbers in messy formats ("(281) 555-1234", "281.555.1234",
// "1-281-555-1234"), and the OS/8x8 protocol handler wants a clean
// E.164-ish string. If no country code and 10 digits, prefix +1.
function normalizePhone(raw) {
  if (!raw) return '';
  let s = String(raw).trim();
  const hasPlus = s.startsWith('+');
  s = s.replace(/[^\d]/g, '');
  if (!s) return '';
  if (!hasPlus && s.length === 10) return `+1${s}`;
  if (!hasPlus && s.length === 11 && s.startsWith('1')) return `+${s}`;
  return hasPlus ? `+${s}` : s;
}
function buildSmsUri(phone) {
  const p = normalizePhone(phone);
  if (!p) return '';
  // 8x8 Work for Desktop registers the `votel://` protocol and exposes
  // `votel://sendsms/<number>` which opens a 1:1 SMS conversation with
  // the number pre-populated. Per 8x8's official docs the `<number>`
  // is passed as plain digits (their tel:// example uses "16692380507"
  // without a "+"), so we strip the leading + here. If we leave the +
  // in, 8x8 opens the app but silently rejects the sendsms deep-link
  // as "Invalid Phone Number" — matching the exact bug the user hit.
  //   Ref: https://docs.8x8.com/8x8WebHelp/8x8-work-for-desktop/Content/workd/use-im-sms-messages.htm
  // Per 8x8's docs this scheme does NOT accept a body/message parameter
  // — we compensate by copying the template to the clipboard on click.
  const digits = p.replace(/^\+/, '');
  return `votel://sendsms/${digits}`;
}
function buildTelUri(phone) {
  const p = normalizePhone(phone);
  return p ? `tel:${p}` : '';
}

function fieldsGrid(children) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>; }

// TextField MUST live at module scope — if it's defined inside
// ClientFormDialog, every keystroke re-runs the component body which
// re-creates TextField as a fresh function reference. React sees a new
// component type on each render and unmounts/remounts the underlying
// <input>, blowing away DOM focus on every character typed (iter 58 bug
// report: Client Name field lost focus after every keystroke).
const TextField = ({ id, label, type = 'text', form, set, readOnly }) => (
  <div>
    <Label className="text-xs text-[#685B72]">{label}</Label>
    <Input
      type={type}
      className="rounded-xl mt-1"
      value={form[id] || ''}
      onChange={set(id)}
      disabled={readOnly}
      data-testid={`field-${id}`}
    />
  </div>
);

const SectionTitle = ({ children, section }) => (
  <div data-section={section} className="col-span-full pt-5 pb-2 mt-2 scroll-mt-28 text-xs uppercase tracking-widest text-[#685B72] font-semibold border-t border-[#E8E5EC]">
    {children}
  </div>
);

// First section title doesn't need a divider above it — the modal header already provides one.
const FirstSectionTitle = ({ children, section }) => (
  <div data-section={section} className="col-span-full pb-2 scroll-mt-28 text-xs uppercase tracking-widest text-[#685B72] font-semibold">
    {children}
  </div>
);

export default function ClientFormDialog({ open, onOpenChange, client, setup = {}, clinicLocations = null, onSave, onDelete, onReactivated, readOnly, focusSection = '' }) {
  const { user } = useAuth();
  const [form, setForm] = useState({});
  const [locations, setLocations] = useState([]);
  // ── All clinics + their outside_locations (iter 90) ───────────────────
  // Cached so LocationField can render options from a DIFFERENT clinic
  // than `form.clinic` when the family wants their eval and their
  // ongoing sessions at different Thrive locations. Fetched once per
  // dialog open. Falls back to `[]` if the request fails; existing
  // single-clinic behaviour continues to work.
  const [allClinics, setAllClinics] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Active payer tree (parent group → children) + flat standalone list,
  // sourced from vault_payers.active=true. Fetched once per open; used by
  // the PayerField in the Onboarding section. Pre-sorted by parent name.
  const [payerTree, setPayerTree] = useState({ groups: [], standalone: [] });
  // Pause-to-Waitlist in-flight state — prevents double-fire while the
  // backend recomputes stage.
  const [pausing, setPausing] = useState(false);
  // Sibling-based payer suggestion (iter 353 enhancement). Fetched on
  // demand when the OD picks an Insurance Type and types a client name
  // without picking a payer yet. One-tap "Apply" writes it into the
  // form. Dismissed suggestions won't re-prompt within this dialog session.
  const [payerSuggestion, setPayerSuggestion] = useState(null);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  // Paste-helper (iter 98). 8x8 Work Desktop's `votel://sendsms/` deep-link
  // opens the app but does NOT reliably populate the SMS window with the
  // number, and there is no supported body/message parameter at all. To
  // guarantee a fast paste flow we surface both the number and the
  // message with dedicated Copy buttons the instant the admin clicks Text.
  const [pasteHelper, setPasteHelper] = useState(null); // { phone, body, copiedField }

  // Undo the most recent paperwork follow-up entry — wired to both the
  // toast Undo action (right after logging) and the persistent "Undo last"
  // button in the Attempt history header. Prevents the double-tap footgun
  // where two rapid clicks push `pw_followups` to 3 and unfairly park the
  // client in "Paperwork Stalled".
  const undoLastFollowup = async () => {
    if (!client?.client_id) return;
    try {
      const r = await api.post(`/clients/${client.client_id}/paperwork-followup/undo`);
      setForm(f => ({
        ...f,
        pw_followups: r.data.pw_followups,
        pw_last_followup_date: r.data.pw_last_followup_date,
        pw_followup_log: r.data.pw_followup_log,
      }));
      const method = r.data.removed?.method || 'Follow-up';
      toast.success(`Undone — removed ${method} attempt (${r.data.pw_followups}/3)`);
    } catch (e) {
      toast.error(errMsg(e, 'Undo failed.'));
    }
  };

  useEffect(() => {
    setForm(client || { service_type: 'Speech', pre_auth_required: 'No', hold_status: 'Active', waitlist_status: 'Not Waitlisted' });
    setConfirmDelete(false);
  }, [client, open]);

  // Payer suggestion from siblings — fires once the OD has set both
  // Insurance Type and Client Name and hasn't picked a payer yet.
  // Debounced to avoid firing per keystroke. Resets dismissed state when
  // either input changes materially.
  useEffect(() => {
    if (!open) return;
    if (form.payer_id) { setPayerSuggestion(null); return; }
    const insType = (form.insurance_type || '').trim();
    const name = (form.client_name || '').trim();
    const isSelfPay = insType.toLowerCase() === 'self-pay' || insType.toLowerCase() === 'self pay';
    if (!insType || !name || isSelfPay || name.length < 3) {
      setPayerSuggestion(null); return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/clients/payer-suggestion', {
          params: { insurance_type: insType, client_name: name },
        });
        if (data && data.payer_id) setPayerSuggestion(data);
        else setPayerSuggestion(null);
      } catch { setPayerSuggestion(null); }
    }, 450);
    return () => clearTimeout(t);
  }, [open, form.insurance_type, form.client_name, form.payer_id]);

  // Reset dismissal when inputs change so a different suggestion can appear.
  useEffect(() => { setSuggestionDismissed(false); }, [form.insurance_type, form.client_name]);
  // Mirrors the behaviour the user asked for: Payer field is irrelevant
  // for self-pay cases, so flipping Insurance Type to Self-Pay forces
  // the Self-Pay bypass flag and blanks any lingering Payer selection.
  useEffect(() => {
    const t = (form.insurance_type || '').toLowerCase();
    if (t === 'self-pay' || t === 'self pay') {
      if (form.self_pay !== 'Yes' || form.payer_id || form.payer_name) {
        setForm(f => ({ ...f, self_pay: 'Yes', payer_id: '', payer_name: '' }));
      }
    }
  }, [form.insurance_type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-transition Client Outcome → "Therapy Started" the moment the OD flips
  // "Therapist Assigned" to Yes. Mirrors the backend rule but gives instant
  // visual feedback so the dropdown updates without the user re-saving.
  // Never overwrite a terminal outcome (Withdrew / Lost / etc).
  useEffect(() => {
    if (form.therapist_assigned === 'Yes'
        && !INACTIVE_OUTCOMES.has(form.client_outcome || '')
        && form.client_outcome !== 'Therapy Started') {
      setForm(f => ({ ...f, client_outcome: 'Therapy Started' }));
    }
  }, [form.therapist_assigned]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill Eval Completed Date the moment the OD toggles Eval Completed
  // to "Yes" — mirrors the backend default so the OD can review + adjust
  // the date INLINE before saving instead of only seeing it after the
  // record round-trips. Prefers the scheduled `eval_date` (that's when
  // the eval almost always happened); falls back to today (CT wall clock)
  // when eval_date is blank. Only fills when the field is currently empty
  // so we never clobber a value the OD has explicitly typed.
  useEffect(() => {
    if (form.eval_completed === 'Yes' && !(form.eval_completed_date || '').trim()) {
      const ctToday = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
      const fallback = (form.eval_date || '').trim() || ctToday;
      setForm(f => ({ ...f, eval_completed_date: fallback }));
    }
  }, [form.eval_completed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch clinicians once per dialog open so evaluator/therapist dropdowns can filter by clinic
  useEffect(() => {
    if (!open) return;
    api.get('/clinicians').then(r => setClinicians(r.data || [])).catch(() => setClinicians([]));
    // ── All-clinics list — feeds the eval/therapy clinic-override dropdowns
    // and gives LocationField the option to source outside_locations from
    // a clinic other than form.clinic (iter 90).
    api.get('/clinics').then(r => setAllClinics(r.data || [])).catch(() => setAllClinics([]));
    // Active payer directory (parent + children) — powers the Payer picker
    // in the Onboarding section when Insurance Type isn't Self-Pay.
    api.get('/clients/payer-options')
      .then(r => setPayerTree({
        groups: r.data?.groups || [],
        standalone: r.data?.standalone || [],
      }))
      .catch(() => setPayerTree({ groups: [], standalone: [] }));
  }, [open]);

  // Deep-link scroll (iter 47) — when the parent passes a focusSection,
  // scroll the corresponding [data-section] header into view once the
  // dialog content has painted. Timeout ensures the collapse/expand and
  // clinician fetches don't fight the scroll for layout.
  useEffect(() => {
    if (!open || !focusSection) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-section="${focusSection}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(t);
  }, [open, focusSection, client?.client_id]);

  // Only these roles are qualified to conduct evaluations. Therapist-assigned falls back to all clinic-assigned staff.
  const EVAL_ROLES = new Set(['Office Director/SLP', 'SLP', 'CF-SLP', 'CF', 'PRN']);

  // Smart evaluator/therapist options — filter to selected clinic, bilingual staff shown first
  const smartClinicianGroups = useMemo(() => {
    const target = form.clinic;
    if (!target || clinicians.length === 0) return null;
    const clinicOnly = clinicians.filter(c => Array.isArray(c.clinics) && c.clinics.includes(target));
    return { clinicOnly };
  }, [form.clinic, clinicians]);

  // Determine which clinic's outside locations to show
  useEffect(() => {
    if (!open) return;
    // If parent passed clinicLocations directly (clinic-view flow), use them
    if (clinicLocations !== null) {
      setLocations(clinicLocations);
      return;
    }
    // Otherwise, fetch based on the currently-selected clinic in the form
    if (!form.clinic) { setLocations([]); return; }
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinics`, { credentials: 'include' });
        const list = await res.json();
        const match = list.find(c => c.name === form.clinic);
        setLocations(match?.outside_locations || []);
      } catch { setLocations([]); }
    })();
  }, [form.clinic, open, clinicLocations]);

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const SelectField = ({ id, label, options, value }) => (
    <div>
      <Label className="text-xs text-[#685B72]">{label}</Label>
      <Select
        value={value || ''}
        onValueChange={(v) => set(id)(v === '__none__' ? '' : v)}
        disabled={readOnly}
      >
        <SelectTrigger className="rounded-xl mt-1" data-testid={`field-${id}`}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__" className="italic text-[#685B72]" data-testid={`field-${id}-none`}>— None —</SelectItem>
          {options.map((o, i) => <SelectItem key={`${o}-${i}`} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  // Smart clinician dropdown: bilingual staff pinned to top, then rest — all filtered to the client's clinic.
  // Falls back to setup.clinicians if the client has no clinic set or clinicians list is empty.
  const SmartClinicianField = ({ id, label }) => {
    const value = form[id] || '';
    const evalOnly = id === 'evaluator';       // gate to Office Director/SLP/CF/CF-SLP only
    // Medicaid gate (iter 41): when the client is a Medicaid case, only
    // evaluators marked Medicaid Approved may appear in the dropdown.
    // Non-medicaid clients still see the full clinic-scoped list.
    const medicaidCase = evalOnly && ((form.insurance_type || '').toLowerCase() === 'medicaid');
    const clinicOnly = smartClinicianGroups?.clinicOnly || [];
    let filtered = evalOnly ? clinicOnly.filter(c => EVAL_ROLES.has(c.role || '')) : clinicOnly;
    if (medicaidCase) {
      filtered = filtered.filter(c => (c.medicaid_approved || 'No') === 'Yes');
    }
    const bilingual = filtered.filter(c => c.bilingual === 'Yes').sort((a, b) => a.name.localeCompare(b.name));
    const others = filtered.filter(c => c.bilingual !== 'Yes').sort((a, b) => a.name.localeCompare(b.name));
    const groups = { bilingual, others, hasAny: filtered.length > 0 };
    const fallback = setup.clinicians || (setup.evaluators || []).map(e => e.name);

    // If we have no clinic-scoped groups yet, degrade gracefully to plain SelectField
    if (!smartClinicianGroups || !groups.hasAny) {
      return (
        <div>
          <Label className="text-xs text-[#685B72]">
            {label}
            {medicaidCase && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#8A3A3A]/15 text-[#8A3A3A] font-semibold" data-testid="medicaid-only-badge">Medicaid Approved only</span>}
            {form.clinic && <span className="ml-1 text-[#685B72]/60">· no {medicaidCase ? 'Medicaid-approved evaluators' : (evalOnly ? 'qualified evaluators' : 'clinicians')} assigned to {form.clinic}</span>}
          </Label>
          <Select value={value} onValueChange={(v) => set(id)(v === '__none__' ? '' : v)} disabled={readOnly}>
            <SelectTrigger className="rounded-xl mt-1" data-testid={`field-${id}`}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__" className="italic text-[#685B72]" data-testid={`field-${id}-none`}>— None —</SelectItem>
              {fallback.map((o, i) => <SelectItem key={`${o}-${i}`} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }

    return (
      <div>
        <Label className="text-xs text-[#685B72]">
          {label}
          {medicaidCase && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#8A3A3A]/15 text-[#8A3A3A] font-semibold" data-testid="medicaid-only-badge">Medicaid Approved only</span>}
          <span className="ml-1 text-[#685B72]/60">· {form.clinic} {evalOnly ? 'evaluators' : 'team'} ({bilingual.length + others.length})</span>
        </Label>
        <Select value={value} onValueChange={(v) => set(id)(v === '__none__' ? '' : v)} disabled={readOnly}>
          <SelectTrigger className="rounded-xl mt-1" data-testid={`field-${id}`}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__" className="italic text-[#685B72]" data-testid={`field-${id}-none`}>— None —</SelectItem>
            {groups.bilingual.length > 0 && (
              <SelectGroup>
                <SelectLabel className="flex items-center gap-1.5 text-[#685B72] font-semibold">
                  <Languages size={12} /> Bilingual (recommended for Spanish-speaking families)
                </SelectLabel>
                {groups.bilingual.map((c, i) => (
                  <SelectItem key={`${c.clinician_id || c.name}-${i}`} value={c.name} data-testid={`opt-eval-${c.name.replace(/\W+/g, '_')}`}>
                    <span className="flex items-center gap-2">
                      <span>{c.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#BAA9CA]/25 text-[#685B72]">{c.role || 'SLP'}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {groups.bilingual.length > 0 && groups.others.length > 0 && <SelectSeparator />}
            {groups.others.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-[#685B72] font-semibold">{form.clinic} {evalOnly ? 'evaluators' : 'team'}</SelectLabel>
                {groups.others.map((c, i) => (
                  <SelectItem key={`${c.clinician_id || c.name}-${i}`} value={c.name} data-testid={`opt-eval-${c.name.replace(/\W+/g, '_')}`}>
                    <span className="flex items-center gap-2">
                      <span>{c.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F1EDF5] text-[#685B72]">{c.role || 'SLP'}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {value && ![...groups.bilingual, ...groups.others].some(c => c.name === value) && (
              <>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel className="text-[#685B72]/70 italic">Historic assignment</SelectLabel>
                  <SelectItem value={value}>{value}</SelectItem>
                </SelectGroup>
              </>
            )}
          </SelectContent>
        </Select>
      </div>
    );
  };

  // Location options for Eval / Therapy dropdowns. Same-name businesses at
  // different addresses (e.g. two "Sunshine Daycare" branches) are common,
  // so the label surfaces the first line of the address alongside the name.
  // If the location has a `nickname` set (iter 45), we render that on the
  // main line instead — Davinder can use short human labels like
  // "ABC — Katy" without renaming the legal business.
  //
  // Iter 90 — accepts an optional overrideClinic. When passed and different
  // from `form.clinic`, we pull outside_locations from THAT clinic instead
  // so a family can have eval at Fry Rd and sessions at Townelake.
  const buildLocationOptions = (overrideClinic) => {
    const clinicName = overrideClinic || form.clinic;
    let outside = locations;
    if (overrideClinic && overrideClinic !== form.clinic) {
      const match = allClinics.find(c => c.name === overrideClinic);
      outside = match?.outside_locations || [];
    }
    return [
      { value: 'In Clinic', name: 'In Clinic', addressFirst: clinicName || '' },
      ...outside.map(l => ({
        value: l.name,
        name: (l.nickname && l.nickname.trim()) || l.name,
        subline: l.nickname && l.nickname.trim() ? l.name : '',
        addressFirst: (l.address || '').split(/[\n,]/)[0].trim(),
      })),
    ];
  };

  // Per-field clinic override — Eval Clinic / Therapy Clinic dropdowns
  // let the user pick a *different* Thrive clinic to source outside
  // locations from. Falls back to `form.clinic` when unset (existing
  // single-clinic behaviour).
  const ClinicOverrideField = ({ id, label, mirrorTo }) => {
    const value = form[id] || '';
    const clinics = (setup.clinics || []).filter(Boolean);
    return (
      <div>
        <Label className="text-xs text-[#685B72]">{label}</Label>
        <Select
          value={value || '__same__'}
          onValueChange={(v) => {
            const next = v === '__same__' ? '' : v;
            setForm(f => {
              const out = { ...f, [id]: next };
              // If the target sub-clinic changes, clear the child location
              // so we don't keep a Fry outside-location tagged to Townelake.
              if (mirrorTo && f[mirrorTo]) out[mirrorTo] = '';
              return out;
            });
          }}
          disabled={readOnly}
        >
          <SelectTrigger className="rounded-xl mt-1" data-testid={`field-${id}`}>
            <SelectValue placeholder={`Same as Main Clinic${form.clinic ? ` (${form.clinic})` : ''}`} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__same__" className="italic text-[#685B72]" data-testid={`field-${id}-same`}>
              Same as Main Clinic{form.clinic ? ` (${form.clinic})` : ''}
            </SelectItem>
            {clinics.map((cn) => (
              <SelectItem
                key={cn}
                value={cn}
                data-testid={`opt-${id}-${cn.replace(/\W+/g, '_')}`}
              >
                {cn}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  const LocationField = ({ id, label, overrideClinic }) => {
    const opts = buildLocationOptions(overrideClinic);
    return (
      <div>
        <Label className="text-xs text-[#685B72]">{label}</Label>
        <Select value={form[id] || ''} onValueChange={(v) => set(id)(v === '__none__' ? '' : v)} disabled={readOnly}>
          <SelectTrigger className="rounded-xl mt-1" data-testid={`field-${id}`}>
            <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__" className="italic text-[#685B72]" data-testid={`field-${id}-none`}>— None —</SelectItem>
            {opts.map((o, i) => (
              <SelectItem
                key={`${o.value}-${i}`}
                value={o.value}
                data-testid={`opt-${id}-${o.value.replace(/\W+/g, '_')}`}
              >
                <span className="flex flex-col">
                  <span className="font-medium text-[#2D2833]">{o.name}</span>
                  {(o.subline || o.addressFirst) && (
                    <span className="text-[10px] text-[#685B72]">
                      {o.subline ? `${o.subline} · ` : ''}{o.addressFirst}
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="client-form-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 flex-wrap">
            <DialogTitle className="font-serif text-2xl text-[#2D2833]">{client?.client_id ? client.client_name : 'New Client'}</DialogTitle>
            {client?.stage && <span className={`stage-badge ${stageClass(client.stage)}`}>{client.stage}</span>}
            {client?.stage === 'Inactive' && client?.inactive_reason && (
              <span className="text-[10px] uppercase tracking-widest text-[#8A3A3A] bg-[#8A3A3A]/15 border border-[#8A3A3A]/30 px-2 py-0.5 rounded-full font-semibold" data-testid="inactive-reason-badge">
                {client.inactive_reason}
              </span>
            )}
            {client?.stage === 'Inactive' && ['admin', 'super_user', 'hr_payroll'].includes(user?.role) && (
              <Button
                size="sm"
                className="ml-auto bg-[#839E97] hover:bg-[#839E97] text-white rounded-xl text-xs h-8"
                data-testid="btn-reactivate"
                onClick={async () => {
                  const note = window.prompt('Add an audit note for reactivation (optional):', '') || '';
                  if (!window.confirm(`Reactivate ${client.client_name}? They'll move to 'Referral Received' with a fresh referral date.`)) return;
                  try {
                    await api.post(`/clients/${client.client_id}/reactivate`, { note });
                    toast.success(`${client.client_name} reactivated → Referral Received.`);
                    onOpenChange(false);
                    // Refresh the parent list so the client's new stage renders.
                    // Prefer an explicit callback; fall back to a soft reload.
                    if (typeof onReactivated === 'function') onReactivated();
                    else window.location.reload();
                  } catch (e) {
                    toast.error(errMsg(e, 'Reactivation failed.'));
                  }
                }}
              >
                Reactivate → Referral Received
              </Button>
            )}
            {/* Pause-to-Waitlist — one-click discontinue-therapy flow for
                clients who finished a round of therapy and now want an
                afternoon spot. Clears therapy fields on the server and
                recomputes stage to Waitlisted. Only shown while the
                client is in an active therapy stage. */}
            {client?.client_id &&
             !readOnly &&
             ['Therapy Started', 'Therapy Scheduled'].includes(client?.stage) && (
              <Button
                size="sm"
                className="ml-auto bg-[#B08A3A] hover:bg-[#8F6E2A] text-white rounded-xl text-xs h-8"
                data-testid="btn-pause-to-waitlist"
                disabled={pausing}
                onClick={async () => {
                  const note = window.prompt(
                    `Pause ${client.client_name} to Waitlist?\n\nThis clears their therapy fields (start date, therapist assignment, sessions scheduled) and moves them to "Waitlisted" stage. Optional note:`,
                    'Child discontinued therapy — waiting for afternoon spot',
                  );
                  if (note === null) return;
                  setPausing(true);
                  try {
                    const r = await api.post(`/clients/${client.client_id}/pause-to-waitlist`, { note });
                    toast.success(`${client.client_name} moved to Waitlist.`);
                    setForm(f => ({ ...f, ...r.data }));
                    onOpenChange(false);
                    if (typeof onReactivated === 'function') onReactivated();
                  } catch (e) {
                    toast.error(errMsg(e, 'Could not pause to Waitlist.'));
                  } finally { setPausing(false); }
                }}
              >
                {pausing ? 'Moving…' : 'Pause → Waitlist'}
              </Button>
            )}
          </div>
        </DialogHeader>

        <ClientSectionTabs showDischarge={form.client_outcome === 'Therapy Started'} focus={open ? focusSection : ''} />

        {fieldsGrid(<>
          <FirstSectionTitle section="client">Client Info</FirstSectionTitle>
          <TextField id="client_name" label="Client Name" form={form} set={set} readOnly={readOnly} />
          <TextField id="dob" label="Date of Birth (DOB)" type="date" form={form} set={set} readOnly={readOnly} />
          <TextField id="phone" label="Parent Phone (for Text/Call)" type="tel" form={form} set={set} readOnly={readOnly} />
          <SelectField id="clinic" label="Main Clinic" options={setup.clinics || []} value={form.clinic} />
          <SelectField id="service_type" label="Service Type" options={setup.service_types || []} value={form.service_type} />
          <SelectField id="referral_source" label="Referral Source" options={setup.referral_sources || []} value={form.referral_source} />
          <TextField id="referral_date" label="Referral Date" type="date" form={form} set={set} readOnly={readOnly} />
          <LocationField id="eval_location" label={`Eval Location${form.eval_clinic ? ` (${form.eval_clinic})` : form.clinic ? ` (${form.clinic})` : ''}`} overrideClinic={form.eval_clinic} />
          <LocationField id="therapy_location" label={`Therapy Location${form.therapy_clinic ? ` (${form.therapy_clinic})` : form.clinic ? ` (${form.clinic})` : ''}`} overrideClinic={form.therapy_clinic} />
          {/* Cross-clinic overrides (iter 90). Optional — when unset the
              location dropdowns above pull from the main clinic. */}
          <ClinicOverrideField id="eval_clinic" label="Eval Clinic (optional override)" mirrorTo="eval_location" />
          <ClinicOverrideField id="therapy_clinic" label="Therapy Clinic (optional override)" mirrorTo="therapy_location" />

          <SectionTitle section="paperwork">Onboarding</SectionTitle>
          <SelectField id="pw_sent" label="Paperwork Sent" options={YESNO} value={form.pw_sent} />
          <SelectField id="pw_completed" label="Paperwork Completed" options={YESNO} value={form.pw_completed} />
          <SelectField id="benefits_verified" label="Benefits Verified" options={YESNO} value={form.benefits_verified} />
          <SelectField id="gfe_sent" label="GFE Sent" options={YESNO} value={form.gfe_sent} />
          <SelectField id="insurance_type" label="Insurance Type" options={setup.insurance_types || []} value={form.insurance_type} />
          <SelectField id="self_pay" label="Self-Pay (bypass pre-auth)" options={YESNO} value={form.self_pay} />

          {/* Payer picker — populated from the active Payer Directory
              (Admin Vault → Payers). Hidden when the client is marked
              Self-Pay since there's no payer to pick. Users can select
              either the parent group (e.g. "BCBS") or any specific
              child plan under it (e.g. "BCBS TX"). Optional field. */}
          {(form.insurance_type || '').toLowerCase() !== 'self-pay' &&
           (form.insurance_type || '').toLowerCase() !== 'self pay' && (
            <div className="col-span-full md:col-span-1" data-testid="payer-field-wrap">
              <Label className="text-xs text-[#685B72]">
                Payer <span className="text-[#685B72]/60 italic">· optional</span>
              </Label>
              {/* Sibling suggestion banner — one tap applies the payer that
                  an existing family member with the same Insurance Type is
                  using. Keeps intake fast for sibling admissions. */}
              {payerSuggestion && !suggestionDismissed && !form.payer_id && (
                <div
                  className="mt-1 mb-1 rounded-lg border border-[#B08A3A]/40 bg-[#FFF6DF] px-2 py-1.5 text-[11px] text-[#5F4A1A] flex items-center gap-2"
                  data-testid="payer-suggestion-banner"
                >
                  <span className="shrink-0">💡</span>
                  <span className="flex-1">
                    <b>{payerSuggestion.sibling_name}</b> uses <b>{payerSuggestion.payer_name}</b>
                    {payerSuggestion.sibling_count > 1 ? ` (+${payerSuggestion.sibling_count - 1} more)` : ''} — apply?
                  </span>
                  <button
                    type="button"
                    className="px-2 py-0.5 rounded-md bg-[#B08A3A] text-white hover:bg-[#8F6E2A] font-semibold"
                    data-testid="payer-suggestion-apply"
                    onClick={() => {
                      setForm(f => ({ ...f, payer_id: payerSuggestion.payer_id, payer_name: payerSuggestion.payer_name }));
                      setPayerSuggestion(null);
                    }}
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    className="text-[#685B72] hover:text-[#2D2833]"
                    data-testid="payer-suggestion-dismiss"
                    onClick={() => setSuggestionDismissed(true)}
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                </div>
              )}
              <Select
                value={form.payer_id || '__none__'}
                onValueChange={(v) => {
                  if (v === '__none__') { setForm(f => ({ ...f, payer_id: '', payer_name: '' })); return; }
                  // Find the display name — scan groups first, then standalone, then parent names.
                  let name = '';
                  for (const g of payerTree.groups) {
                    if (g.parent_payer_id === v) { name = g.parent_name; break; }
                    const kid = (g.children || []).find(c => c.payer_id === v);
                    if (kid) { name = kid.name; break; }
                  }
                  if (!name) {
                    const s = payerTree.standalone.find(p => p.payer_id === v);
                    if (s) name = s.name;
                  }
                  setForm(f => ({ ...f, payer_id: v, payer_name: name }));
                }}
                disabled={readOnly}
              >
                <SelectTrigger className="rounded-xl mt-1" data-testid="field-payer_id">
                  <SelectValue placeholder="Select payer (optional)" />
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  <SelectItem value="__none__" className="italic text-[#685B72]" data-testid="field-payer-none">— None —</SelectItem>
                  {payerTree.groups.map((g) => (
                    <SelectGroup key={g.parent_payer_id}>
                      <SelectLabel className="text-[#685B72] font-semibold">{g.parent_name}</SelectLabel>
                      <SelectItem
                        value={g.parent_payer_id}
                        data-testid={`opt-payer-parent-${g.parent_name.replace(/\W+/g, '_')}`}
                      >
                        <span className="font-medium">{g.parent_name} <span className="text-[10px] text-[#685B72]">(parent group)</span></span>
                      </SelectItem>
                      {(g.children || []).map((c) => (
                        <SelectItem
                          key={c.payer_id}
                          value={c.payer_id}
                          data-testid={`opt-payer-child-${c.name.replace(/\W+/g, '_')}`}
                        >
                          <span className="pl-3 text-[#2D2833]">↳ {c.name}</span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                  {payerTree.standalone.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="text-[#685B72] font-semibold">Other payers</SelectLabel>
                      {payerTree.standalone.map((p) => (
                        <SelectItem
                          key={p.payer_id}
                          value={p.payer_id}
                          data-testid={`opt-payer-standalone-${p.name.replace(/\W+/g, '_')}`}
                        >
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {/* Historic: current payer_id not in active directory */}
                  {form.payer_id &&
                   !payerTree.groups.some(g => g.parent_payer_id === form.payer_id || (g.children || []).some(c => c.payer_id === form.payer_id)) &&
                   !payerTree.standalone.some(p => p.payer_id === form.payer_id) && (
                    <SelectGroup>
                      <SelectLabel className="text-[#685B72]/70 italic">Historic selection</SelectLabel>
                      <SelectItem value={form.payer_id}>{form.payer_name || form.payer_id}</SelectItem>
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Paperwork follow-up log & CTA — only meaningful when paperwork is pending */}
          {client?.client_id && form.pw_sent === 'Yes' && form.pw_completed !== 'Yes' && (
            <div className="col-span-full rounded-xl border border-[#E8E5EC] bg-[#FAF8FB] p-4" data-testid="pw-followup-panel">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#685B72] font-semibold flex items-center gap-1.5">
                    <PhoneCall size={12} /> Paperwork Follow-ups
                  </div>
                  <div className="mt-1 text-sm text-[#2D2833]">
                    <span className="font-serif text-2xl">{form.pw_followups || 0}</span>
                    <span className="text-[#685B72]"> / 3 attempts</span>
                    {form.pw_last_followup_date && <span className="text-xs text-[#685B72] ml-2">· last: {form.pw_last_followup_date}</span>}
                  </div>
                  <div className="text-xs text-[#685B72] mt-1">
                    {(form.pw_followups || 0) >= 3
                      ? 'Max follow-ups reached — consider marking client Lost or Withdrew.'
                      : 'After 3 follow-ups with no completion, dispose as Lost / Withdrew.'}
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex items-end gap-2 flex-wrap">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Method</Label>
                      <Select value={form._followup_method || 'Text'} onValueChange={v => setForm(f => ({ ...f, _followup_method: v }))}>
                        <SelectTrigger className="rounded-xl mt-1 h-9 w-[120px] text-xs" data-testid="followup-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['Text', 'Email', 'Call', 'Voicemail', 'In-person', 'Other'].map(m => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <Label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Note (optional)</Label>
                      <input
                        type="text"
                        value={form._followup_note || ''}
                        onChange={e => setForm(f => ({ ...f, _followup_note: e.target.value }))}
                        placeholder="e.g. left voicemail, will call back Tue"
                        className="mt-1 h-9 w-full rounded-xl border border-[#E8E5EC] bg-white px-3 text-xs text-[#2D2833] placeholder:text-[#685B72] focus:outline-none focus:border-[#685B72]"
                        maxLength={500}
                        data-testid="followup-note"
                      />
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          const r = await api.post(
                            `/clients/${client.client_id}/paperwork-followup`,
                            { method: form._followup_method || 'Text', note: form._followup_note || '' },
                          );
                          setForm(f => ({
                            ...f,
                            pw_followups: r.data.pw_followups,
                            pw_last_followup_date: r.data.pw_last_followup_date,
                            pw_followup_log: r.data.pw_followup_log,
                            _followup_note: '',  // clear input so the next attempt starts fresh
                          }));
                          toast.success(`Follow-up logged (${r.data.pw_followups}/3)`, {
                            duration: 10000,
                            action: {
                              label: 'Undo',
                              onClick: () => undoLastFollowup(),
                            },
                          });
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                      }}
                      className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl h-9"
                      data-testid="log-followup-btn"
                      disabled={(form.pw_followups || 0) >= 3}
                    >
                      <PhoneCall size={14} className="mr-2" /> Log follow-up
                    </Button>
                  </div>
                )}
              </div>

              {/* One-tap SMS + Call via the 8x8 Work desktop app (iter 94) —
                  the sms:/tel: URIs are routed by the OS/browser protocol
                  handler. When 8x8 Work is registered as the sms:/tel:
                  handler, clicking these pops the 8x8 compose window with
                  number + body pre-populated. Auto-logs an attempt in the
                  history so admins never lose track of what they sent. */}
              {!readOnly && (
                <div className="mt-3 pt-3 border-t border-dashed border-[#E8E5EC] flex flex-wrap items-center gap-2" data-testid="pw-quick-contact">
                  {(() => {
                    const hasPhone = !!normalizePhone(form.phone);
                    const childName = (form.client_name || '').split(' ')[0] || 'your child';
                    const smsBody = `Hi! This is Thrive Speech Therapy following up about ${childName}. Our schedule is filling up quickly, so we wanted to reach out before openings are gone. Once we have his paperwork completed on the SimplePractice portal, we can lock in a spot for him. Let us know if you need anything — we're glad to help. Thanks!`;
                    const canFire = hasPhone && (form.pw_followups || 0) < 3;
                    const smsUri = canFire ? buildSmsUri(form.phone) : '';
                    const telUri = canFire ? buildTelUri(form.phone) : '';
                    const copyBodySync = () => {
                      try {
                        if (navigator?.clipboard?.writeText) {
                          navigator.clipboard.writeText(smsBody).catch(() => {});
                          return true;
                        }
                      } catch { /* ignore */ }
                      return false;
                    };
                    const autoLog = async (method, note) => {
                      try {
                        const r = await api.post(
                          `/clients/${client.client_id}/paperwork-followup`,
                          { method, note },
                        );
                        setForm(f => ({
                          ...f,
                          pw_followups: r.data.pw_followups,
                          pw_last_followup_date: r.data.pw_last_followup_date,
                          pw_followup_log: r.data.pw_followup_log,
                        }));
                        toast.success(`${method} logged (${r.data.pw_followups}/3)`, {
                          duration: 10000,
                          action: {
                            label: 'Undo',
                            onClick: () => undoLastFollowup(),
                          },
                        });
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Log failed'); }
                    };
                    return (
                      <>
                        <span className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold mr-1">Quick contact</span>
                        <a
                          href={smsUri || undefined}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!hasPhone) { toast.error('Add a Parent Phone above first.'); return; }
                            if ((form.pw_followups || 0) >= 3) { toast.error('Max follow-ups reached.'); return; }
                            copyBodySync();
                            // Fire the protocol handler explicitly.
                            if (smsUri) window.location.href = smsUri;
                            // Surface the paste helper so admins have a
                            // guaranteed 1-click copy for BOTH the number
                            // and the message — 8x8's SMS window doesn't
                            // reliably pre-fill either, so this is the
                            // fastest usable path.
                            setPasteHelper({
                              phone: normalizePhone(form.phone),
                              body: smsBody,
                              copiedField: 'body',  // body was already copied above
                            });
                            autoLog('Text', `SMS: ${smsBody.slice(0, 80)}${smsBody.length > 80 ? '…' : ''}`);
                          }}
                          className={`inline-flex items-center gap-1.5 rounded-xl h-9 px-3 text-xs font-semibold transition-colors ${
                            canFire
                              ? 'bg-[#685B72] text-white hover:bg-[#685B72]'
                              : 'bg-[#E8E5EC] text-[#685B72] cursor-not-allowed'
                          }`}
                          data-testid="text-family-btn"
                          title={hasPhone ? 'Opens 8x8 Work with the number pre-filled. Message is copied to clipboard — paste with ⌘V / Ctrl+V (8x8 does not accept a body parameter).' : 'Add a Parent Phone above to enable'}
                        >
                          <MessageSquare size={14} /> Text this family
                        </a>
                        <a
                          href={telUri || undefined}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!hasPhone) { toast.error('Add a Parent Phone above first.'); return; }
                            if ((form.pw_followups || 0) >= 3) { toast.error('Max follow-ups reached.'); return; }
                            if (telUri) window.location.href = telUri;
                            autoLog('Call', 'Placed call from Thrive Ops Hub');
                          }}
                          className={`inline-flex items-center gap-1.5 rounded-xl h-9 px-3 text-xs font-semibold transition-colors ${
                            canFire
                              ? 'bg-[#839E97] text-white hover:bg-[#839E97]'
                              : 'bg-[#E8E5EC] text-[#685B72] cursor-not-allowed'
                          }`}
                          data-testid="call-family-btn"
                          title={hasPhone ? 'Opens 8x8 Work (or your default tel: handler) to place the call' : 'Add a Parent Phone above to enable'}
                        >
                          <Phone size={14} /> Call
                        </a>
                        {!hasPhone && (
                          <span className="text-[10px] text-[#8A3A3A] ml-1" data-testid="no-phone-hint">
                            Add a Parent Phone in Client Info to enable
                          </span>
                        )}
                        {hasPhone && (
                          <span className="text-[10px] text-[#685B72] ml-1 italic" data-testid="paste-hint">
                            Tap Text to open 8x8 — number & message shown below with 1-click copy
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Paste-helper panel (iter 98). Appears the instant the admin
                  taps Text so they can copy the number and message into
                  8x8's compose window with two 1-click copies. This is the
                  bulletproof path — 8x8's votel://sendsms/<n> deep-link
                  behavior varies by version and there's no body param. */}
              {!readOnly && pasteHelper && (
                <div
                  className="mt-3 rounded-xl border border-[#BAA9CA]/40 bg-[#FAF6FB] p-4"
                  data-testid="pw-paste-helper"
                >
                  <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-bold">
                      Paste into 8x8 →
                    </div>
                    <button
                      type="button"
                      onClick={() => setPasteHelper(null)}
                      className="text-[10px] text-[#685B72] hover:text-[#2D2833] underline"
                      data-testid="paste-helper-close"
                    >
                      Close
                    </button>
                  </div>

                  <div className="grid gap-2">
                    {/* Number row */}
                    <div className="flex items-center gap-2 rounded-lg bg-white border border-[#E8E5EC] p-2">
                      <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold w-16 shrink-0">Number</div>
                      <div className="flex-1 font-mono text-sm text-[#2D2833] truncate" data-testid="paste-helper-phone">
                        {pasteHelper.phone}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(pasteHelper.phone);
                            setPasteHelper(p => p ? { ...p, copiedField: 'phone' } : p);
                            toast.success('Number copied — paste into 8x8', { duration: 3000 });
                          } catch { toast.error('Clipboard blocked — select and copy manually'); }
                        }}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-[#685B72] text-white hover:bg-[#52475A]"
                        data-testid="paste-helper-copy-phone"
                      >
                        {pasteHelper.copiedField === 'phone' ? <Check size={12} /> : <Copy size={12} />}
                        {pasteHelper.copiedField === 'phone' ? 'Copied' : 'Copy'}
                      </button>
                    </div>

                    {/* Message row */}
                    <div className="rounded-lg bg-white border border-[#E8E5EC] p-2">
                      <div className="flex items-start gap-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold w-16 shrink-0 pt-1">Message</div>
                        <div className="flex-1 text-xs text-[#2D2833] whitespace-pre-wrap leading-relaxed" data-testid="paste-helper-body">
                          {pasteHelper.body}
                        </div>
                      </div>
                      <div className="flex justify-end mt-2">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(pasteHelper.body);
                              setPasteHelper(p => p ? { ...p, copiedField: 'body' } : p);
                              toast.success('Message copied — paste into 8x8', { duration: 3000 });
                            } catch { toast.error('Clipboard blocked — select and copy manually'); }
                          }}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-[#685B72] text-white hover:bg-[#52475A]"
                          data-testid="paste-helper-copy-body"
                        >
                          {pasteHelper.copiedField === 'body' ? <Check size={12} /> : <Copy size={12} />}
                          {pasteHelper.copiedField === 'body' ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 text-[10px] text-[#685B72] italic">
                    In 8x8 Work → New Text Message → paste the number in "To:" and the message in the chat box.
                  </div>
                </div>
              )}

              {/* Attempt history (iter 93) — every text/email/call ever made */}
              {Array.isArray(form.pw_followup_log) && form.pw_followup_log.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#E8E5EC]" data-testid="pw-followup-log">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Attempt history</div>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => undoLastFollowup()}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#8A3A3A] hover:text-[#8A3A3A] hover:bg-[#8A3A3A]/10 font-semibold px-2 py-0.5 rounded-full border border-[#8A3A3A]/40 transition-colors"
                        title="Reverse the most recent follow-up entry — useful if the button was tapped twice by mistake."
                        data-testid="undo-last-followup-btn"
                      >
                        <RotateCcw size={11} /> Undo last
                      </button>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {[...form.pw_followup_log].reverse().map((row, i) => {
                      const METHOD_COLORS = {
                        Text: '#685B72', Email: '#685B72', Call: '#839E97',
                        Voicemail: '#B08A3A', 'In-person': '#8A3A3A', Other: '#685B72',
                        Unspecified: '#685B72',
                      };
                      const when = row.at ? new Date(row.at).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      }) : '—';
                      return (
                        <div key={i} className="flex items-start gap-3 text-xs bg-white rounded-lg border border-[#E8E5EC] p-2" data-testid={`pw-followup-entry-${i}`}>
                          <span
                            className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full text-white uppercase tracking-widest shrink-0"
                            style={{ background: METHOD_COLORS[row.method] || '#685B72' }}
                          >
                            {row.method || 'Unspecified'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[#685B72]">
                              {row.note ? row.note : <span className="italic text-[#685B72]">no note</span>}
                            </div>
                            <div className="text-[10px] text-[#685B72] mt-0.5">
                              {when}{row.by ? <> · {row.by}</> : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <SectionTitle section="preauth">Pre-Authorization</SectionTitle>
          <SelectField id="pre_auth_required" label="Pre-Auth Required" options={YESNO} value={form.pre_auth_required} />
          <SelectField id="pre_auth_submitted" label="Pre-Auth Submitted" options={YESNO} value={form.pre_auth_submitted} />
          <TextField id="auth_sub_date" label="Auth Submission Date" type="date" form={form} set={set} readOnly={readOnly} />
          <SelectField id="pre_auth_status" label="Pre-Auth Status" options={setup.pre_auth_status || []} value={form.pre_auth_status} />
          <SelectField id="session_limit" label="Session Limit" options={setup.session_limits || []} value={form.session_limit} />

          <SectionTitle section="evaluation">Evaluation</SectionTitle>
          <SelectField id="eval_scheduled" label="Eval Scheduled" options={YESNO} value={form.eval_scheduled} />
          <TextField id="eval_date" label="Eval Date" type="date" form={form} set={set} readOnly={readOnly} />
          <SmartClinicianField id="evaluator" label="Evaluator" />
          <SelectField id="eval_completed" label="Eval Completed" options={YESNO} value={form.eval_completed} />
          {/* Eval Completed Date — surfaces the moment the OD flips Eval
              Completed to Yes so they can back-date/reschedule the actual
              completion. Backend auto-stamps today's date on first flip. */}
          {form.eval_completed === 'Yes' && (
            <TextField id="eval_completed_date" label="Eval Completed Date" type="date" form={form} set={set} readOnly={readOnly} />
          )}

          {/* Eval reschedule history */}
          {Array.isArray(form.eval_reschedule_log) && form.eval_reschedule_log.length > 0 && (
            <div className="col-span-full rounded-xl border border-[#E8E5EC] bg-[#FAF8FB] p-4" data-testid="reschedule-log">
              <div className="text-xs uppercase tracking-widest text-[#685B72] font-semibold flex items-center gap-1.5">
                <History size={12} /> Eval reschedule history ({form.eval_reschedule_log.length})
              </div>
              <ul className="mt-2 space-y-1 text-xs text-[#2D2833]">
                {form.eval_reschedule_log.map((entry, i) => (
                  <li key={`${entry.at}-${i}`} className="font-mono">
                    <span className="text-[#8A3A3A]">{fmtDate(entry.old_date)}</span>
                    {' → '}
                    <span className="text-[#839E97]">{fmtDate(entry.new_date)}</span>
                    <span className="text-[#685B72] ml-2">by {entry.changed_by}</span>
                    {entry.reason && <span className="text-[#685B72]"> · "{entry.reason}"</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {client?.client_id && form.eval_date && form.eval_completed !== 'Yes' && (
            <div className="col-span-full">
              <Label className="text-xs text-[#685B72]">Reschedule reason (only used if you change the eval date above)</Label>
              <Input className="rounded-xl mt-1" value={form.reschedule_reason || ''} onChange={set('reschedule_reason')} disabled={readOnly} data-testid="field-reschedule-reason" placeholder="e.g. Family conflict, sick child, evaluator out" />
            </div>
          )}

          <SectionTitle section="therapy">Therapy</SectionTitle>
          <SelectField id="therapist_assigned" label="Therapist Assigned" options={YESNO} value={form.therapist_assigned} />
          <SelectField id="therapy_sess_sched" label="Therapy Sessions Scheduled" options={YESNO} value={form.therapy_sess_sched} />
          <TextField id="therapy_start_date" label="Therapy Start Date" type="date" form={form} set={set} readOnly={readOnly} />

          <SectionTitle section="hold">Status</SectionTitle>
          <SelectField id="waitlist_status" label="Waitlist Status" options={setup.waitlist_status || []} value={form.waitlist_status} />
          <SelectField id="hold_status" label="Hold Status" options={['Active', 'On Hold']} value={form.hold_status} />
          <SelectField id="hold_reason" label="Hold Reason" options={setup.hold_reasons || []} value={form.hold_reason} />
          <TextField id="hold_date" label="Hold Date" type="date" form={form} set={set} readOnly={readOnly} />
          <SelectField id="client_outcome" label="Client Outcome" options={setup.client_outcomes || []} value={form.client_outcome} />
          <SelectField id="outcome_reason" label="Outcome Reason" options={setup.outcome_reasons || []} value={form.outcome_reason} />

          {/* Post-Therapy Discharge — only shown once the client is actively in therapy.
              Captures why + when families stop service so ODs can track drop reasons
              per clinic per month in the Executive BI dashboard. */}
          {form.client_outcome === 'Therapy Started' && (
            <>
              <SectionTitle section="discharge">Post-Therapy Discharge</SectionTitle>
              <SelectField id="discharge_reason" label="Discharge Reason" options={setup.discharge_reasons || []} value={form.discharge_reason} />
              <TextField id="discharge_date" label="Service Termination Date" type="date" form={form} set={set} readOnly={readOnly} />
              <div className="col-span-full text-[11px] text-[#685B72] -mt-2">
                Fill in when the family stops service. Contributes to the clinic's monthly discharge report.
              </div>
            </>
          )}

          <div className="col-span-full">
            <Label className="text-xs text-[#685B72]">Notes</Label>
            <Textarea className="rounded-xl mt-1" rows={3} value={form.notes || ''} onChange={set('notes')} disabled={readOnly} data-testid="field-notes" />
          </div>
        </>)}

        <DialogFooter className="gap-2 sm:justify-between">
          {/* Left-aligned Delete cluster — super_user + admin only, plus a confirm gate. */}
          <div className="flex-1 flex justify-start">
            {!readOnly && client?.client_id && DELETE_ROLES.has(user?.role) && onDelete && (
              confirmDelete ? (
                <div className="flex items-center gap-2" data-testid="delete-confirm-row">
                  <span className="text-xs text-[#8A3A3A]">Delete this client?</span>
                  <Button
                    variant="ghost"
                    onClick={() => setConfirmDelete(false)}
                    data-testid="delete-cancel"
                    className="h-8 px-3 text-xs"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => { await onDelete(client); }}
                    className="bg-[#8A3A3A] hover:bg-[#8A3A3A] text-white rounded-xl h-8 px-3 text-xs"
                    data-testid="delete-confirm"
                  >
                    <Trash2 size={12} className="mr-1.5" /> Delete
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-xl border-[#8A3A3A]/40 text-[#8A3A3A] hover:bg-[#8A3A3A]/10"
                  data-testid="delete-client-btn"
                >
                  <Trash2 size={14} className="mr-2" /> Delete client
                </Button>
              )
            )}
          </div>
          <div className="flex gap-2 items-center">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="dialog-cancel">Cancel</Button>
            {!readOnly && (
              <Button className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl" onClick={() => onSave(form)} data-testid="dialog-save">
                {client?.client_id ? 'Save Changes' : 'Create Client'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
